import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { EsPageShell, EsSection } from "@/components/site/EsPageShell";
import { ImageLightbox } from "@/components/site/ImageLightbox";
import { MedicalReviewBanner } from "@/components/site/MedicalReview";
import { ProcedureFigure } from "@/components/site/ProcedureFigure";
import { SectionCta } from "@/components/site/SectionCta";
import type { EsRevisionSpoke } from "@/lib/es-revision-spokes";
import { revisionGalleryResponsive } from "@/lib/service-responsive";

/** Distinct analytics label for the contact block in each spoke's location section. */
const LOCATION_CTA_LABELS: Record<EsRevisionSpoke["page"], string> = {
  revisionsSleeve: "cta_sleeve_revision_location_es",
  revisionsBypass: "cta_bypass_revision_location_es",
  revisionsBand: "cta_band_revision_location_es",
  revisionsTore: "cta_tore_location_es",
};

/**
 * Renders inline markdown-style links inside body copy, e.g.
 * "…[cómo la anatomía de la manga causa reflujo](/es/manga-gastrica#reflujo)…".
 */
function renderInlineLinks(text: string) {
  const pattern = /\[([^\]]+)\]\((\/[^)]*)\)/g;
  const nodes: Array<string | ReactNode> = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));
    const target = match[2];
    const hashIndex = target.indexOf("#");
    const to = hashIndex >= 0 ? target.slice(0, hashIndex) : target;
    const hash = hashIndex >= 0 ? target.slice(hashIndex + 1) : undefined;
    nodes.push(
      <Link
        key={`${match[2]}-${match.index}`}
        to={to}
        hash={hash}
        className="underline underline-offset-2 hover:text-foreground"
      >
        {match[1]}
      </Link>,
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex === 0) return text;
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

/**
 * Shared layout for the Spanish revision spokes (/es/cirugia-de-revision/*).
 * Mirrors the English RevisionSpokePage: banner under the H1, prose sections,
 * optional clinical gallery, FAQ list and links back to the hub and siblings.
 */
export function EsRevisionSpokePage({ spoke }: { spoke: EsRevisionSpoke }) {
  const isTore = spoke.page === "revisionsTore";
  const renderBullets = (section: EsRevisionSpoke["sections"][number]) => section.bullets ? (
    <ul className="mt-3 list-disc space-y-2 pl-6 text-foreground/85">
      {section.bullets.map((bullet) => <li key={bullet}>{renderInlineLinks(bullet)}</li>)}
    </ul>
  ) : null;
  return (
    <EsPageShell
      page={spoke.page}
      title={spoke.h1}
      intro={spoke.intro}
      banner={
        <div className="mx-auto mt-4 max-w-3xl text-left">
          <MedicalReviewBanner lang="es" />
        </div>
      }
    >
      {spoke.sections.map((section) => (
        <EsSection key={section.id} id={section.id} heading={section.heading}>
          {section.paragraphs.map((paragraph, paragraphIndex) => (
            <div key={paragraph}>
              <p className="mt-3 text-foreground/85">{renderInlineLinks(paragraph)}</p>
              {section.bulletsAfterParagraph === paragraphIndex ? renderBullets(section) : null}
              {section.figureAfterParagraph?.index === paragraphIndex ? (
                <ProcedureFigure
                  media={section.figureAfterParagraph.media}
                  lang="es"
                  lightbox
                  className="mx-auto mt-6 md:max-w-[70%]"
                  sizes="(min-width: 768px) 300px, calc(100vw - 3rem)"
                  imgClassName="h-auto w-full rounded-lg bg-muted"
                  captionClassName="mt-2 text-center text-sm text-foreground/70"
                />
              ) : null}
              {section.figurePair?.afterParagraph === paragraphIndex ? (
                <section
                  aria-label={section.id === "por-que-regresa-el-peso" ? "Comparación de estudios con contraste de reservorios gástricos dilatados y normales" : section.id === "reflujo" ? "Serie gastrointestinal superior antes y después de la reparación de hernia hiatal con conversión a bypass" : "Comparación laparoscópica de una manga normal y una manga dilatada"}
                  className={`mt-6 grid items-start gap-3 md:gap-4 ${section.figurePair.items.length === 3 ? "relative left-1/2 w-[calc(100vw-3rem)] max-w-[960px] -translate-x-1/2 grid-cols-3" : "grid-cols-2"}`}
                >
                  {section.figurePair.items.map((media) => (
                    <ProcedureFigure
                      key={media}
                      media={media}
                      lang="es"
                      lightbox
                      sizes={section.figurePair?.items.length === 3 ? "(min-width: 1024px) 320px, (min-width: 640px) 370px, calc((100vw - 4rem) / 3)" : "(min-width: 640px) 370px, calc((100vw - 4rem) / 2)"}
                      imgClassName="h-auto w-full rounded-lg bg-muted"
                      captionClassName="mt-1 line-clamp-2 text-center text-xs text-foreground/70 md:mt-2 md:line-clamp-none md:text-sm"
                    />
                  ))}
                </section>
              ) : null}
            </div>
          ))}
          {section.bulletsAfterParagraph === -1 ? renderBullets(section) : null}

          {section.id === "en-virginia" || section.id === "tore-en-el-norte-de-virginia" ? (
            <>
              <p className="mt-3 text-foreground/85">
                ¿Preguntas sobre su revisión? Llame a nuestro consultorio de Herndon al{" "}
                <a href="tel:+17037099771" aria-label="Llame a Virginia Bariatric Surgery al 703-709-9771" className="font-medium text-brand-logo hover:underline">
                  703-709-9771
                </a>
                .
              </p>
              <SectionCta to="/es/contacto" label={LOCATION_CTA_LABELS[spoke.page]}>Solicite una Consulta</SectionCta>
            </>
          ) : null}

          {section.gallery ? (
            <figure className="mt-6">
              <ul className="grid list-none grid-cols-3 items-start gap-3 p-0">
                {section.gallery.images.map((image) => (
                  <li key={image.src}>
                    <ImageLightbox
                      fullSrc={image.src}
                      fullWidth={image.width}
                      fullHeight={image.height}
                      alt={image.alt}
                      caption={image.caption}
                      enlargeLabel={`Ampliar imagen: ${image.caption}`}
                      closeLabel="Cerrar imagen ampliada"
                    >
                      <img
                        src={revisionGalleryResponsive[image.src]?.src ?? image.src}
                        srcSet={revisionGalleryResponsive[image.src]?.srcSet}
                        sizes="(min-width: 640px) 30vw, calc((100vw - 3rem) / 3)"
                        alt={image.alt}
                        width={image.width}
                        height={image.height}
                        loading="lazy"
                        decoding="async"
                        className="h-auto w-full rounded-xl border border-border object-cover"
                      />
                    </ImageLightbox>
                    <p className="mt-1 line-clamp-2 text-xs text-muted-foreground md:mt-2 md:line-clamp-none md:text-sm">{image.caption}</p>
                  </li>
                ))}
              </ul>
              <figcaption className="mt-3 text-sm text-muted-foreground">
                {section.gallery.caption}
              </figcaption>
            </figure>
          ) : null}
          {spoke.page === "revisionsSleeve" && section.id === "manga-a-bypass" && section.gallery ? (
            <SectionCta to="/es/contacto" label="cta_sleeve_revision_after_bypass_gallery_es">Hable de sus opciones con el Dr. Fitzer</SectionCta>
          ) : null}

          {section.figure ? (
            <ProcedureFigure
              media={section.figure}
              lang="es"
              lightbox={section.figure === "revisionPouchEs"}
              className="mt-6"
              imgClassName="h-auto w-full rounded-lg"
              captionClassName="mt-2 text-center text-sm text-foreground/70"
              sizes="(min-width: 1024px) 800px, 100vw"
            />
          ) : null}
          {spoke.page === "revisionsSleeve" && section.id === "sadi-switch-duodenal" ? (
            <SectionCta to="/es/contacto" label="cta_sleeve_revision_after_options_es">Averigüe qué revisión le conviene</SectionCta>
          ) : spoke.page === "revisionsSleeve" && section.id === "resultados" ? (
            <SectionCta to="/es/requisitos-para-cirugia" label="cta_sleeve_revision_after_results_es">Vea si cumple los requisitos para una revisión</SectionCta>
          ) : spoke.page === "revisionsSleeve" && section.id === "seguro-y-costo" ? (
            <SectionCta to="/es/cobertura-de-seguro" label="cta_sleeve_revision_after_insurance_es">Revise la cobertura de su revisión</SectionCta>
          ) : null}
        </EsSection>
      ))}

      <EsSection id="preguntas-frecuentes" heading={isTore ? "TORe: preguntas frecuentes" : "Preguntas frecuentes"}>
        <dl className="mt-2">
          {spoke.faqs.map((faq, i) => (
            <div key={faq.q} id={`faq-${i + 1}`} className="border-b border-border py-4">
              <dt className="font-semibold text-brand-deep">{faq.q}</dt>
              <dd className="mt-2 text-foreground/85">
                {faq.a.split(/\n{2,}/).map((para, i) => (
                  <p key={i} className={i > 0 ? "mt-3" : undefined}>{para}</p>
                ))}
              </dd>
            </div>
          ))}
        </dl>
      </EsSection>

      {isTore && spoke.relatedPages ? (
        <EsSection id="paginas-relacionadas" heading="Páginas relacionadas">
          <ul className="list-disc space-y-2 pl-6">
            {spoke.relatedPages.map((page) => (
              <li key={page.to}><Link to={page.to} className="font-medium text-brand-logo hover:underline">{page.label}</Link></li>
            ))}
          </ul>
        </EsSection>
      ) : null}

      <EsSection id="siguiente-paso" heading={isTore ? "Dé el siguiente paso" : "Siguiente paso"}>
        {spoke.closing ? <p className="mt-3 text-foreground/85">{spoke.closing}</p> : null}
        {spoke.byline ? <p className="mt-6 text-sm italic text-foreground/70">{spoke.byline}</p> : null}
        {isTore ? (
          <p className="mt-3 text-center"><Link to="/es/contacto" className="inline-flex items-center justify-center rounded-full bg-primary px-7 py-3 font-medium text-primary-foreground hover:opacity-90">Solicitar una Consulta</Link></p>
        ) : null}
        {!isTore ? <>
        <p className="mt-3 text-foreground/85">
          ¿No va a usar el seguro? Consulte el{" "}
          <Link
            to="/es/cirugia-bariatrica-de-pago-directo"
            hash="costo-cirugia-de-revision"
            className="font-medium text-brand-logo hover:underline"
          >
            costo de la cirugía de revisión
          </Link>{" "}
          con pago propio: un estimado de $25,400, que cubre el honorario del cirujano, un día de
          hospital en INOVA Fair Oaks Hospital, un estimado de anestesia y un año de seguimiento
          de rutina con el Dr. Fitzer.
        </p>

        <ul className="mt-4 flex list-none flex-wrap justify-center gap-4 p-0 text-sm">
          <li>
            <Link to="/es/cirugia-de-revision" className="font-medium text-brand-logo hover:underline">
              Todas las opciones de revisión
            </Link>
          </li>
          <li>
            <Link to={spoke.sibling.to} className="font-medium text-brand-logo hover:underline">
              {spoke.sibling.label}
            </Link>
          </li>
          <li>
            <Link
              to={spoke.relatedProcedure.to}
              className="font-medium text-brand-logo hover:underline"
            >
              {spoke.relatedProcedure.label}
            </Link>
          </li>
          <li>
            <Link to="/es/cobertura-de-seguro" className="font-medium text-brand-logo hover:underline">
              Cobertura de seguro
            </Link>
          </li>
        </ul>
        </> : null}
      </EsSection>
      {spoke.references?.length ? (
        <EsSection id="referencias-medicas" heading="Referencias médicas">
          <ol className="list-decimal space-y-2 pl-6 text-sm text-foreground/70">
            {spoke.references.map((reference) => <li key={reference}>{reference}</li>)}
          </ol>
        </EsSection>
      ) : null}
    </EsPageShell>
  );
}
